import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContactService } from '../services/contactService';
import { usePageTitle } from '../hooks/usePageTitle';
import { SEOMeta } from '../templates/SEOMeta';
import { BaseLayout } from '../templates/BaseLayout';
import { logger } from '../utils/logger';

interface ContactForm {
  email: string;
  name: string;
  subject: string;
  description: string;
}

const Contact: React.FC = () => {
  const { t } = useTranslation();
  usePageTitle(t('contactPage.pageTitle'));
  const [activeTab, setActiveTab] = useState<'business' | 'recommendations'>('business');
  const [businessForm, setBusinessForm] = useState<ContactForm>({
    email: '',
    name: '',
    subject: '',
    description: ''
  });
  const [recommendationForm, setRecommendationForm] = useState({ description: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleBusinessSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await ContactService.createContact({
        ...businessForm,
        type: 'BUSINESS',
        isAnonymous: false
      });

      setSubmitStatus('success');
      setBusinessForm({ email: '', name: '', subject: '', description: '' });
    } catch (error) {
      logger.error('Error submitting business form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRecommendationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await ContactService.createContact({
        description: recommendationForm.description,
        type: 'RECOMMENDATION',
        isAnonymous: true
      });

      setSubmitStatus('success');
      setRecommendationForm({ description: '' });
    } catch (error) {
      logger.error('Error submitting recommendation form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BaseLayout>
      <SEOMeta
        title={t('contactPage.seo.title')}
        description={t('contactPage.seo.description')}
        canonical="https://el-impostor.com/contact"
      />
      <div className="min-h-screen p-4 pt-8 bg-blue-50 dark:bg-gray-950">
        <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary-800 dark:text-primary-200 mb-2 text-shadow">
            {t('contactPage.header.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-300 text-lg">
            {t('contactPage.header.subtitle')}
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="card mb-8">
          <div className="flex border-b border-gray-200 dark:border-gray-800 mb-6">
            <button
              onClick={() => setActiveTab('business')}
              className={`px-6 py-3 font-medium text-sm transition-colors duration-200 border-b-2 ${
                activeTab === 'business'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {t('contactPage.tabs.business')}
            </button>
            <button
              onClick={() => setActiveTab('recommendations')}
              className={`px-6 py-3 font-medium text-sm transition-colors duration-200 border-b-2 ml-8 ${
                activeTab === 'recommendations'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {t('contactPage.tabs.recommendations')}
            </button>
          </div>

          {/* Success/Error Messages */}
          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex">
                <span className="text-green-500 mr-2">✅</span>
                <span className="text-green-700 dark:text-green-200 font-medium">
                  {t('contactPage.messages.success')}
                </span>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex">
                <span className="text-red-500 mr-2">❌</span>
                <span className="text-red-700 dark:text-red-200 font-medium">
                  {t('contactPage.messages.error')}
                </span>
              </div>
            </div>
          )}

          {/* Business Form */}
          {activeTab === 'business' && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">
                  {t('contactPage.business.title')}
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                  {t('contactPage.business.description')}
                </p>
              </div>

              <form onSubmit={handleBusinessSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="business-email" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      {t('contactPage.business.emailLabel')}
                    </label>
                    <input
                      type="email"
                      id="business-email"
                      required
                      className="input-field"
                      placeholder={t('contactPage.business.emailPlaceholder')}
                      value={businessForm.email}
                      onChange={(e) => setBusinessForm(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label htmlFor="business-name" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      {t('contactPage.business.nameLabel')}
                    </label>
                    <input
                      type="text"
                      id="business-name"
                      required
                      className="input-field"
                      placeholder={t('contactPage.business.namePlaceholder')}
                      value={businessForm.name}
                      onChange={(e) => setBusinessForm(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="business-subject" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    {t('contactPage.business.subjectLabel')}
                  </label>
                  <input
                    type="text"
                    id="business-subject"
                    required
                    className="input-field"
                    placeholder={t('contactPage.business.subjectPlaceholder')}
                    value={businessForm.subject}
                    onChange={(e) => setBusinessForm(prev => ({ ...prev, subject: e.target.value }))}
                  />
                </div>

                <div>
                  <label htmlFor="business-description" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    {t('contactPage.business.descriptionLabel')}
                  </label>
                  <textarea
                    id="business-description"
                    required
                    rows={6}
                    className="input-field resize-none"
                    placeholder={t('contactPage.business.descriptionPlaceholder')}
                    value={businessForm.description}
                    onChange={(e) => setBusinessForm(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full md:w-auto px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t('contactPage.business.submitSending') : t('contactPage.business.submit')}
                </button>
              </form>
            </div>
          )}

          {/* Recommendations Form */}
          {activeTab === 'recommendations' && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">
                  {t('contactPage.recommendations.title')}
                </h2>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                  <div className="flex">
                    <span className="text-blue-500 mr-2">ℹ️</span>
                    <div className="text-blue-700 dark:text-blue-200">
                      <p className="font-medium mb-1">{t('contactPage.recommendations.infoTitle')}</p>
                      <p className="text-sm">
                        {t('contactPage.recommendations.infoText')}
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 dark:text-gray-300">
                  {t('contactPage.recommendations.description')}
                </p>
              </div>

              <form onSubmit={handleRecommendationSubmit} className="space-y-6">
                <div>
                  <label htmlFor="recommendation-description" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    {t('contactPage.recommendations.textareaLabel')}
                  </label>
                  <textarea
                    id="recommendation-description"
                    required
                    rows={8}
                    className="input-field resize-none"
                    placeholder={t('contactPage.recommendations.textareaPlaceholder')}
                    value={recommendationForm.description}
                    onChange={(e) => setRecommendationForm(prev => ({ ...prev, description: e.target.value }))}
                  />
                  <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    {t('contactPage.recommendations.examples')}
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
                  <h3 className="font-medium text-gray-800 dark:text-gray-100 mb-2">{t('contactPage.recommendations.privacyTitle')}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {t('contactPage.recommendations.privacyText')}
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full md:w-auto px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t('contactPage.recommendations.submitSending') : t('contactPage.recommendations.submit')}
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Footer Info */}
        <div className="text-center text-gray-500 dark:text-gray-400 text-sm">
          <p>
            {t('contactPage.footer.preferOtherPrefix')}{' '}
            <span className="font-medium">contact@impostorgame.com</span>
          </p>
        </div>
      </div>
      </div>
    </BaseLayout>
  );
};

export default Contact;
